import React from 'react';
import { Button, CircularProgress } from '@mui/material';
import { fontSize, minWidth, styled, width } from '@mui/system';

const colors = {
  primary: {
    main: '#995a00',
    hover: '#BF7000',
    focus: '#995A00',
    active: '#663C00',
    outlined: '#995A0080',
    label: '#ffffff',
    hoverbg: '#995A000D',
    focusedbg: '#995A001F',
    activebg: '#995A004D'
  },
  secondary: {
    main: '#FFA525',
    hover: '#FFC066',
    focus: '#FFA525',
    active: '#FF9600',
    outlined: '#FFC066',
    label: '#ffffff',
    hoverbg: '#FFA52514',
    focusedbg: '#FFA5251F',
    activebg: '#FFA52533'
  },
  error: {
    main: '#cc0000',
    hover: '#FF3333',
    focus: '#cc0000',
    active: '#B20000',
    outlined: '#cc0000',
    label: '#ffffff',
    hoverbg: '#CC000014',
    focusedbg: '#CC000014',
    activebg: '#CC00001F'
  },
  success: {
    main: '#008000',
    hover: '#6CD96C',
    focus: '#008000',
    active: '#006600',
    outlined: '#008000',
    label: '#ffffff',
    hoverbg: '#00800014',
    focusedbg: '#0080001F',
    activebg: '#00800033'
  },
  neutral: {
    main: '#333333',
    hover: '#E5E5E5',
    focus: '#D9D9D9',
    active: '#8C8C8C',
    outlined: '#8C8C8C',
    label: '#333333',
    hoverbg: '#E5E5E5',
    focusedbg: '#D9D9D9',
    activebg: '#8C8C8C'
  }
};

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) =>
    prop !== 'customcolor' && prop !== 'variantStyle'
})(({ customcolor = 'primary', variant = 'filled', disabled, size, startIcon, endIcon  }) => {
  const colorSet = colors[customcolor] || colors.primary;
  const base = {
    borderRadius: 8,
    fontWeight: 600,
    fontSize: size === 'small' ? '13px !important' : '16px !important',
    lineHeight: size === 'small' ? '20px !important' : '24px !important',
    textTransform: 'none',
    height: size === 'small' ? '32px !important' : '40px !important',
    minHeight: size === 'small' ? '32px !important' : '40px !important',
    padding: size === 'small' ? (startIcon || endIcon ? '4px 12px' : '6px 12px') : '8px 24px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.2s ease-in-out',
    '& .MuiButton-startIcon': {
      margin: 0,
    },
    '& .MuiButton-icon': {
      width:'24px',
      height:'24px',
      display:'flex',
      alignItems:'center',
      justifyContent:'center'
    },
    '& .MuiButton-endIcon': {
      margin: 0,
    },
    ...(disabled && {
      backgroundColor: '#E5E5E5',
      color: '#8C8C8C',
      border: variant === 'outlined' ? `1px solid #ccc` : 'none',
      cursor: 'not-allowed'
    }),
    // color: colorSet.label,
  };

  if (variant === 'filled' && !disabled) {
    base.backgroundColor = colorSet.main;
    base.color = colorSet.label;
    base['&:hover'] = { backgroundColor: colorSet.hover };
    base['&:active'] = { backgroundColor: colorSet.active };
  }

  if (variant === 'outlined' && !disabled) {
    base.backgroundColor = 'transparent';
    base.border = `1px solid ${colorSet.outlined}`;
    base.color = colorSet.main;
    base['&:hover'] = {
      backgroundColor: colorSet.hoverbg,
      border: `1px solid ${colorSet.outlined}`
    };
    base['&:active'] = {
      backgroundColor: colorSet.activebg,
      border: `1px solid ${colorSet.outlined}`
    };
    base['&:focused'] = {
      backgroundColor: colorSet.focusedbg,
      border: `1px solid ${colorSet.outlined}`
    };
  }

  if (variant === 'text' && !disabled) {
    base.backgroundColor = 'transparent';
    base.color = colorSet.main;
    base['&:hover'] = {
      backgroundColor: colorSet.hoverbg,
    };
    base['&:active'] = {
      backgroundColor: colorSet.activebg,
    };
  }

  // if (isFocused && !disabled) {
  //   base.boxShadow = `0 0 0 3px ${colorSet.focus}`;
  // }
  return base;
});

const CustomButton = ({
  variant = 'filled',
  color = 'primary',
  label,
  disabled = false,
  loading = false,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  onClick,
  size = 'medium', // 'small', 'medium'
  ...rest
}) => {
  const [isFocused, setIsFocused] = React.useState(false);

  // When loading is true, show spinner and disable the button
  const isDisabled = disabled || loading;
  
  // Determine which icon to show based on loading state
  const getStartIcon = () => {
    if (loading) {
      return <CircularProgress size={16} sx={{ color: 'currentColor' }} />;
    }
    return LeftIcon ? <LeftIcon /> : null;
  };

  return (
    <StyledButton
      variant={variant}
      customcolor={color}
      disabled={isDisabled}
      startIcon={getStartIcon()}
      endIcon={!loading && RightIcon ? <RightIcon /> : null}
      isFocused={isFocused}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onClick={onClick}
      disableRipple
      size={size}
      {...rest}
    >
      {loading ? 'Loading...' : label}
    </StyledButton>
  );
};

export default CustomButton;
